import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TrainerSidebar from '../../components/ui/TrainerSidebar';
import SessionMonitorHeader from '../../components/ui/SessionMonitorHeader';
import ExerciseCard from './components/ExerciseCard';
import ExerciseFilters from './components/ExerciseFilters';
import ExerciseForm from './components/ExerciseForm';
import ExcelImportModal from './components/ExcelImportModal';
import ExerciseStats from './components/ExerciseStats';

const ExerciseDatabase = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Mock exercise data
  const [exercises, setExercises] = useState([
    {
      id: 1,
      name: "Seated Leg Extensions",
      description: "Strengthens quadriceps muscles while seated, ideal for seniors with mobility limitations. Helps improve knee stability and walking strength.",
      category: "strength",
      type: "dynamic",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
      hasVideo: true,
      videoUrl: "https://example.com/video1.mp4",
      difficultyLevels: [
        { phase: "beginner", reps: "8-10", load: "Light" },
        { phase: "intermediate", reps: "12-15", load: "Moderate" },
        { phase: "advanced", reps: "15-20", load: "Heavy" }
      ],
      isActive: true,
      instructions: "Sit with back against pad, extend legs slowly, hold briefly, then lower with control.",
      equipment: "Leg extension machine",
      targetMuscles: "Quadriceps",
      precautions: "Avoid if recent knee surgery or severe arthritis",
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-08-20')
    },
    {
      id: 2,
      name: "Arm Circles",
      description: "Gentle shoulder mobility exercise that improves range of motion and circulation. Perfect warm-up activity for upper body workouts.",
      category: "flexibility",
      type: "dynamic",
      image: "https://images.pexels.com/photos/3768916/pexels-photo-3768916.jpeg?w=400",
      hasVideo: false,
      difficultyLevels: [
        { phase: "beginner", reps: "10-15", load: "Bodyweight" },
        { phase: "intermediate", reps: "15-20", load: "Light weights" },
        { phase: "advanced", reps: "20-25", load: "Moderate weights" }
      ],
      isActive: true,
      instructions: "Stand with arms extended, make small circles forward, then backward.",
      equipment: "None required",
      targetMuscles: "Shoulders, Upper back",
      precautions: "Start with small circles, avoid if shoulder impingement",
      createdAt: new Date('2024-02-10'),
      updatedAt: new Date('2024-08-18')
    },
    {
      id: 3,
      name: "Balance Training",
      description: "Single-leg standing exercise to improve balance and prevent falls. Essential for maintaining independence in daily activities.",
      category: "balance",
      type: "static",
      image: "https://images.pixabay.com/photos/2017/08/07/14/02/people-2604149_1280.jpg?w=400",
      hasVideo: true,
      videoUrl: "https://example.com/video3.mp4",
      difficultyLevels: [
        { phase: "beginner", reps: "10-15 sec", load: "Support available" },
        { phase: "intermediate", reps: "20-30 sec", load: "Light support" },
        { phase: "advanced", reps: "30-45 sec", load: "No support" }
      ],
      isActive: true,
      instructions: "Stand on one foot, hold position, use support as needed.",
      equipment: "Chair or wall for support",
      targetMuscles: "Core, Ankles, Legs",
      precautions: "Always have support nearby, avoid if severe balance issues",
      createdAt: new Date('2024-03-05'),
      updatedAt: new Date('2024-08-15')
    },
    {
      id: 4,
      name: "Chest Press Machine",
      description: "Safe upper body strengthening exercise using machine support. Builds chest, shoulder, and arm strength with controlled movement.",
      category: "strength",
      type: "dynamic",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
      hasVideo: false,
      difficultyLevels: [
        { phase: "beginner", reps: "6-8", load: "Light" },
        { phase: "intermediate", reps: "10-12", load: "Moderate" },
        { phase: "advanced", reps: "12-15", load: "Heavy" }
      ],
      isActive: false,
      instructions: "Sit with back supported, push handles forward, return slowly.",
      equipment: "Chest press machine",
      targetMuscles: "Chest, Shoulders, Triceps",
      precautions: "Avoid if shoulder injury, start with light weight",
      createdAt: new Date('2024-01-20'),
      updatedAt: new Date('2024-07-10')
    },
    {
      id: 5,
      name: "Seated Marching",
      description: "Low-impact cardiovascular exercise performed while seated. Improves circulation and leg strength without standing.",
      category: "cardio",
      type: "dynamic",
      image: "https://images.pexels.com/photos/3768916/pexels-photo-3768916.jpeg?w=400",
      hasVideo: true,
      videoUrl: "https://example.com/video5.mp4",
      difficultyLevels: [
        { phase: "beginner", reps: "20-30", load: "Slow pace" },
        { phase: "intermediate", reps: "40-50", load: "Moderate pace" },
        { phase: "advanced", reps: "60-80", load: "Fast pace" }
      ],
      isActive: true,
      instructions: "Sit tall, lift knees alternately as if marching in place.",
      equipment: "Sturdy chair",
      targetMuscles: "Hip flexors, Core",
      precautions: "Maintain good posture, stop if dizzy",
      createdAt: new Date('2024-02-28'),
      updatedAt: new Date('2024-08-12')
    },
    {
      id: 6,
      name: "Ankle Pumps",
      description: "Simple circulation exercise to prevent blood clots and improve ankle mobility. Can be done anywhere, anytime.",
      category: "mobility",
      type: "dynamic",
      image: "https://images.pixabay.com/photos/2017/08/07/14/02/people-2604149_1280.jpg?w=400",
      hasVideo: false,
      difficultyLevels: [
        { phase: "beginner", reps: "10-15", load: "Gentle" },
        { phase: "intermediate", reps: "20-25", load: "Moderate" },
        { phase: "advanced", reps: "30-40", load: "Full range" }
      ],
      isActive: true,
      instructions: "Point toes up and down, then make circles with feet.",
      equipment: "None required",
      targetMuscles: "Calves, Ankles",
      precautions: "Gentle movements only, avoid if foot injury",
      createdAt: new Date('2024-03-15'),
      updatedAt: new Date('2024-08-08')
    }
  ]);

  // Filter exercises based on current filters
  const filteredExercises = exercises?.filter(exercise => {
    const matchesSearch = exercise?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         exercise?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || exercise?.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || 
                             exercise?.difficultyLevels?.some(level => level?.phase === selectedDifficulty);
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'active' && exercise?.isActive) ||
                         (selectedStatus === 'inactive' && !exercise?.isActive);

    return matchesSearch && matchesCategory && matchesDifficulty && matchesStatus;
  });

  const handleAddExercise = () => {
    setEditingExercise(null);
    setShowExerciseForm(true);
  };

  const handleEditExercise = (exercise) => {
    setEditingExercise(exercise);
    setShowExerciseForm(true);
  };

  const handleViewExercise = (exercise) => {
    // Navigate to exercise detail view or show modal
    console.log('Viewing exercise:', exercise?.name);
  };

  const handleToggleStatus = (exercise) => {
    setExercises(prev => prev?.map(ex => 
      ex?.id === exercise?.id 
        ? { ...ex, isActive: !ex?.isActive, updatedAt: new Date() }
        : ex
    ));
  };

  const handleDeleteExercise = (exercise) => {
    if (window.confirm(`Are you sure you want to delete "${exercise?.name}"? This action cannot be undone.`)) {
      setExercises(prev => prev?.filter(ex => ex?.id !== exercise?.id));
    }
  };

  const handleSaveExercise = (exerciseData) => {
    setIsLoading(true);
    
    setTimeout(() => {
      if (editingExercise) {
        // Update existing exercise
        setExercises(prev => prev?.map(ex => 
          ex?.id === editingExercise?.id 
            ? { ...exerciseData, id: editingExercise?.id, updatedAt: new Date() }
            : ex
        ));
      } else {
        // Add new exercise
        const newExercise = {
          ...exerciseData,
          id: Math.max(...exercises?.map(ex => ex?.id)) + 1,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setExercises(prev => [...prev, newExercise]);
      }
      
      setIsLoading(false);
      setShowExerciseForm(false);
      setEditingExercise(null);
    }, 1500);
  };

  const handleImportExercises = (importResults) => {
    console.log('Import completed:', importResults);
    // In a real app, this would update the exercises list with imported data
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSelectedStatus('all');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <TrainerSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      {/* Header */}
      <SessionMonitorHeader sidebarCollapsed={sidebarCollapsed} />
      {/* Main Content */}
      <main 
        className={`
          transition-all duration-300 ease-out pt-16
          ${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          lg:${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          md:${sidebarCollapsed ? 'ml-16' : 'ml-72'}
          sm:${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}
      >
        <div className="p-6 space-y-6 mt-4">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Exercise Database</h1>
              <p className="text-muted-foreground mt-1">
                Manage your comprehensive exercise library with images, videos, and difficulty levels
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowImportModal(true)}
                className="breathing-focus"
              >
                <Icon name="Upload" size={20} className="mr-2" />
                Import Excel
              </Button>
              
              <Button
                onClick={handleAddExercise}
                className="breathing-focus"
              >
                <Icon name="Plus" size={20} className="mr-2" />
                Add Exercise
              </Button>
            </div>
          </div>

          {/* Exercise Statistics */}
          <ExerciseStats exercises={exercises} />

          {/* Filters */}
          <ExerciseFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedDifficulty={selectedDifficulty}
            onDifficultyChange={setSelectedDifficulty}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            onClearFilters={handleClearFilters}
          />

          {/* Exercise Grid */}
          <div className="space-y-4">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredExercises?.length} of {exercises?.length} exercises
              </p>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/training-plan-builder')}
                  className="breathing-focus"
                >
                  <Icon name="FileText" size={16} className="mr-2" />
                  Create Training Plan
                </Button>
              </div>
            </div>

            {/* Exercise Cards */}
            {filteredExercises?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredExercises?.map((exercise) => (
                  <ExerciseCard
                    key={exercise?.id}
                    exercise={exercise}
                    onEdit={handleEditExercise}
                    onView={handleViewExercise}
                    onToggleStatus={handleToggleStatus}
                    onDelete={handleDeleteExercise}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={48} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No exercises found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm || selectedCategory !== 'all' || selectedDifficulty !== 'all' || selectedStatus !== 'all'
                    ? 'Try adjusting your filters or search terms' :'Get started by adding your first exercise'
                  }
                </p>
                <div className="flex items-center justify-center space-x-3">
                  {(searchTerm || selectedCategory !== 'all' || selectedDifficulty !== 'all' || selectedStatus !== 'all') && (
                    <Button
                      variant="outline"
                      onClick={handleClearFilters}
                      className="breathing-focus"
                    >
                      <Icon name="X" size={16} className="mr-2" />
                      Clear Filters
                    </Button>
                  )}
                  <Button
                    onClick={handleAddExercise}
                    className="breathing-focus"
                  >
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Exercise
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Exercise Form Modal */}
      {showExerciseForm && (
        <ExerciseForm
          exercise={editingExercise}
          onSave={handleSaveExercise}
          onCancel={() => {
            setShowExerciseForm(false);
            setEditingExercise(null);
          }}
          isLoading={isLoading}
        />
      )}
      {/* Excel Import Modal */}
      {showImportModal && (
        <ExcelImportModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImportExercises}
        />
      )}
    </div>
  );
};

export default ExerciseDatabase;